import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ACTION_BLUE } from '../theme/theme';

interface FileUploadProps {
  imageSrc?: React.ReactNode | string;
  uploadText?: string;
  onFilesSelected?: (files: File[]) => void;
  width?: string; // Custom width
  height?: string; // Custom height
  backgroundColor?: string; // Custom background color
  direction?: 'ltr' | 'rtl'; // Custom text direction
  flexDirection?: 'column' | 'row'; // Custom flex direction
  acceptedFileTypes?: string[]; // Accepted file types
  showFileInfo?: boolean;
  compactView?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  imageSrc = 'default-upload-icon.png',
  uploadText = 'Drag and drop a file here, or click to browse',
  onFilesSelected,
  width = '362px', // Default width
  height = '120px', // Default height
  backgroundColor = 'white', // Default background color
  direction = 'ltr', // Default text direction
  flexDirection = 'column', // Default flex direction
  acceptedFileTypes = ['.jpg', '.jpeg', '.png'], // Default accepted file types
  showFileInfo = true,
  compactView = false,
}) => {
  const [files, setFiles] = useState<{ file: File; progress: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // One object url per File, cached and revoked on unmount. Creating them
  // inline in render mints a fresh url on every pass and never frees it.
  const previewUrlsRef = useRef(new Map<File, string>());

  const previewUrl = (file: File): string => {
    const cached = previewUrlsRef.current.get(file);

    if (cached) {
      return cached;
    }

    const url = URL.createObjectURL(file);
    previewUrlsRef.current.set(file, url);

    return url;
  };

  useEffect(() => {
    const urls = previewUrlsRef.current;

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const handleFileChange = (incomingFiles: File[]) => {
    const validFiles = incomingFiles.filter((file) => validateFileType(file));

    if (validFiles.length === 0) {
      setError(
        'No valid files selected. Please upload files of the correct type.',
      );
      return;
    }

    setError(null);
    const updatedFiles = validFiles.map((file) => ({ file, progress: 0 }));
    setFiles(updatedFiles);

    if (onFilesSelected) {
      onFilesSelected(validFiles);
    }

    // Simulate file upload progress for each file
    updatedFiles.forEach((_, index) => simulateProgress(index));
  };

  const validateFileType = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    return fileExtension && acceptedFileTypes.includes(`.${fileExtension}`);
  };

  const simulateProgress = (fileIndex: number) => {
    let progress = 0;
    const interval = setInterval(() => {
      if (progress >= 100) {
        clearInterval(interval);
      } else {
        progress += 10;
        setFiles((prevFiles) =>
          prevFiles.map((f, idx) =>
            idx === fileIndex ? { ...f, progress } : f,
          ),
        );
      }
    }, 200);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const incomingFiles = Array.from(event.dataTransfer.files);
    handleFileChange(incomingFiles);
  };

  const preventDefault = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <Box
      sx={{
        width: { xs: '310px', md: width },
        height: height, // Custom height prop
        borderRadius: '8px',
        display: 'flex',
        flexDirection: flexDirection, // Custom flex direction prop
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: backgroundColor,
        border: '2px dotted #A6AAB5',
        textAlign: 'center',
        padding: '16px',
        color: '#A6AAB5',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        direction: direction, // Custom text direction prop
      }}
      onClick={handleBoxClick}
      onDrop={handleDrop}
      onDragOver={preventDefault}
      onDragEnter={preventDefault}
      onDragLeave={preventDefault}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={acceptedFileTypes.join(',')}
        onChange={(e) => handleFileChange(Array.from(e.target.files || []))}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {files.length === 0 && (
        <>
          <img
            src={imageSrc as string}
            alt="upload icon"
            style={{
              width: '30px',
              height: '30px',
              marginBottom: '8px',
            }}
          />
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', fontFamily: 'IRANYekanXFaNum' }}
          >
            {uploadText}
          </Typography>
        </>
      )}

      {files.map((fileData, index) =>
        showFileInfo ? (
          <Box
            key={index}
            sx={{
              width: '100%',
              mt: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              padding: '1px',
              borderRadius: '4px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', color: '#333' }}
            >
              {fileData.file.name} ({(fileData.file.size / 1024).toFixed(2)} KB)
            </Typography>
            <IconButton
              onClick={() => handleRemoveFile(0)}
              size="small"
              sx={{
                border: '1px solid #A6AAB5',
                borderRadius: '20%',
                padding: '4px',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0', // رنگ پس‌زمینه در زمان هاور (اختیاری)
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <LinearProgress
              variant="determinate"
              value={fileData.progress}
              sx={{
                width: '40%',
                mt: 1,
                backgroundColor: '#A6AAB5',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: ACTION_BLUE,
                },
              }}
            />
          </Box>
        ) : !compactView ? (
          <React.Fragment key={index}>
            <img
              src={imageSrc as string}
              alt="upload icon"
              style={{
                width: '30px',
                height: '30px',
                marginBottom: '8px',
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', fontFamily: 'IRANYekanXFaNum' }}
            >
              {uploadText}
            </Typography>
          </React.Fragment>
        ) : (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <IconButton
              onClick={() => handleRemoveFile(0)}
              size="small"
              sx={{
                border: '1px solid #A6AAB5',
                borderRadius: '20%',
                padding: '4px',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', color: '#333' }}
            >
              {fileData.file.name}
            </Typography>
            <Box
              component="img"
              src={previewUrl(files[0].file)}
              alt="uploaded file"
              sx={{
                width: '35px',
                height: '35px',
                borderRadius: '4px',
                objectFit: 'cover',
                marginRight: '8px',
              }}
            />
          </Box>
        ),
      )}
    </Box>
  );
};

export default FileUpload;
